import { Injectable, signal, computed, OnDestroy } from '@angular/core';
import { Subscription, take } from 'rxjs';
import { PathwaysService } from 'src/app/services/learner/pathways.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import {
  IPathwayRunStepResponse,
  IPathwayCompleteStepResponse,
  IPathwayNextStepResponse,
} from 'src/app/interfaces/ipathway-enrollment';
import { PathwayConstants } from 'src/app/utils/constants/pathway/pathway.constants';

export type OrchestratorFlowStatus =
  | 'idle'
  | 'starting'
  | 'running'
  | 'completing'
  | 'fetching_next'
  | 'next_available'
  | 'step_completed'
  | 'pathway_completed'
  | 'redirected'
  | 'blocked'
  | 'blocked_step_active'
  | 'error';

@Injectable()
export class PathwayOrchestratorService implements OnDestroy {
  readonly hasError = signal<boolean>(false);
  readonly errorMessage = signal<string>('');
  readonly flowStatus = signal<OrchestratorFlowStatus>('idle');

  readonly isLoading = computed(() =>
    ['starting', 'completing', 'fetching_next'].includes(this.flowStatus())
  );

  readonly runStepResponse = signal<IPathwayRunStepResponse | null>(null);
  readonly completeStepResponse = signal<IPathwayCompleteStepResponse | null>(null);
  readonly nextStepResponse = signal<IPathwayNextStepResponse | null>(null);

  private activeApiSubscription: Subscription | null = null;

  constructor(
    private readonly pathwaysService: PathwaysService,
    private readonly loggerService: LoggerService
  ) { }

  runStep(enrollmentUuid: string, enrollmentStepUuid: string): void {
    if (!enrollmentUuid || !enrollmentStepUuid) {
      this.setErrorState('Missing enrollmentUuid or enrollmentStepUuid for step execution');
      return;
    }

    this.prepareForNewRequest();
    this.runStepResponse.set(null);
    this.flowStatus.set('starting');

    this.activeApiSubscription = this.pathwaysService.runStepByEnrollmentUuid(enrollmentUuid, enrollmentStepUuid).subscribe({
      next: (response: IPathwayRunStepResponse) => {
        if (response.status === PathwayConstants.API_STATUS_STARTED) {
          this.flowStatus.set('running');
          this.runStepResponse.set(response);
        } else if (response.status === PathwayConstants.API_STATUS_BLOCKED) {
          this.runStepResponse.set(response);
          if (response.reason === PathwayConstants.REASON_ANOTHER_STEP_ACTIVE) {
            this.flowStatus.set('blocked_step_active');
          } else {
            this.flowStatus.set('blocked');
            this.errorMessage.set(response.message || `Step blocked: ${response.reason}`);
            this.hasError.set(true);
          }
          this.loggerService.error('[ORCHESTRATOR] Step execution blocked', response);
        } else if (response.status === PathwayConstants.API_STATUS_ERROR) {
          const msg = response.message || `Error running step: ${response.reason}`;
          this.setErrorState(msg);
        } else {
          this.setErrorState(`Unexpected runStep status: ${response.status}`);
        }
      },
      error: (error) => this.setErrorState(error),
      complete: () => { this.activeApiSubscription = null; }
    });
  }

  completeStep(enrollmentUuid: string, enrollmentStepUuid: string): void {
    if (!enrollmentUuid || !enrollmentStepUuid) {
      this.setErrorState('Missing enrollmentUuid or enrollmentStepUuid for completing step');
      return;
    }

    this.prepareForNewRequest();
    this.completeStepResponse.set(null);
    this.flowStatus.set('completing');

    this.activeApiSubscription = this.pathwaysService.completeStepByEnrollmentUuid(enrollmentUuid, enrollmentStepUuid).subscribe({
      next: (response: IPathwayCompleteStepResponse) => {
        this.completeStepResponse.set(response);

        if (response.status === PathwayConstants.API_STATUS_PREAMBLE_COMPLETED) {
          this.flowStatus.set('step_completed');
        } else if (
          response.status === PathwayConstants.API_STATUS_COMPLETED &&
          response.reason === PathwayConstants.REASON_PATHWAY_COMPLETED
        ) {
          this.flowStatus.set('pathway_completed');
        } else if (response.status === PathwayConstants.API_STATUS_COMPLETED) {
          this.flowStatus.set('step_completed');
        } else if (response.status === PathwayConstants.API_STATUS_REQUIREMENTS_NOT_MET) {
          const msg = response.message || `Requirements not met: ${response.requirementFailureReason}`;
          this.setErrorState(msg);
        } else if (response.status === PathwayConstants.API_STATUS_BLOCKED) {
          if (response.reason === PathwayConstants.REASON_STEP_ALREADY_COMPLETED) {
            this.flowStatus.set('step_completed');
          } else {
            this.flowStatus.set('blocked');
            this.errorMessage.set(response.message || `Step completion blocked: ${response.reason}`);
            this.hasError.set(true);
          }
        } else if (response.status === PathwayConstants.API_STATUS_ERROR) {
          const msg = response.message || `Error completing step: ${response.reason}`;
          this.setErrorState(msg);
        } else {
          this.setErrorState(`Unexpected complete step response status: ${response.status}`);
        }
      },
      error: (error) => this.setErrorState(error),
      complete: () => { this.activeApiSubscription = null; }
    });
  }

  completeStepSilently(enrollmentUuid: string, enrollmentStepUuid: string): void {
    if (!enrollmentUuid || !enrollmentStepUuid) {
      this.loggerService.error('[ORCHESTRATOR] Missing enrollmentUuid or enrollmentStepUuid for completeStepSilently');
      return;
    }

    this.pathwaysService.completeStepByEnrollmentUuid(enrollmentUuid, enrollmentStepUuid)
      .pipe(take(1))
      .subscribe({
        next: (response: IPathwayCompleteStepResponse) => {
          if (
            response.status === PathwayConstants.API_STATUS_COMPLETED &&
            response.reason === PathwayConstants.REASON_PATHWAY_COMPLETED
          ) {
            this.flowStatus.set('pathway_completed');
          }
        },
        error: (error) => {
          this.loggerService.error('[ORCHESTRATOR ERROR] silently completing step', error);
        }
      });
  }

  getNextStep(enrollmentUuid: string): void {
    if (!enrollmentUuid) {
      this.setErrorState('Missing enrollmentUuid for getNextStep');
      return;
    }

    this.prepareForNewRequest();
    this.nextStepResponse.set(null);
    this.flowStatus.set('fetching_next');

    this.activeApiSubscription = this.pathwaysService.getNextStepByEnrollmentUuid(enrollmentUuid).subscribe({
      next: (response: IPathwayNextStepResponse) => {
        this.nextStepResponse.set(response);

        if (response.status === PathwayConstants.API_STATUS_NEXT_AVAILABLE) {
          this.flowStatus.set('next_available');
        } else if (response.status === PathwayConstants.API_STATUS_COMPLETED) {
          this.flowStatus.set('pathway_completed');
        } else if (response.status === PathwayConstants.API_STATUS_BLOCKED) {
          this.flowStatus.set('blocked');
          this.errorMessage.set(response.message || `Pathway next step blocked: ${response.reason}`);
          this.hasError.set(true);
        } else if (response.status === PathwayConstants.API_STATUS_REDIRECTED) {
          this.flowStatus.set('redirected');
        } else {
          this.setErrorState(`Unexpected getNextStep response status: ${response.status}`);
        }
      },
      error: (error) => this.setErrorState(error),
      complete: () => { this.activeApiSubscription = null; }
    });
  }

  resetState(): void {
    this.prepareForNewRequest();
    this.flowStatus.set('idle');
    this.runStepResponse.set(null);
    this.completeStepResponse.set(null);
    this.nextStepResponse.set(null);
  }

  private prepareForNewRequest(): void {
    this.activeApiSubscription?.unsubscribe();
    this.activeApiSubscription = null;
    this.clearErrorState();
  }

  private setErrorState(error: unknown): void {
    const errorMsg = error instanceof Error
      ? error.message
      : (error as any)?.message || String(error);

    this.errorMessage.set(errorMsg);
    this.hasError.set(true);
    this.flowStatus.set('error');

    this.loggerService.error('[ORCHESTRATOR ERROR]', errorMsg);
  }

  private clearErrorState(): void {
    this.hasError.set(false);
    this.errorMessage.set('');
  }

  ngOnDestroy(): void {
    this.activeApiSubscription?.unsubscribe();
    this.activeApiSubscription = null;
  }
}
